/**
 * Client billing emails built on one layout. Pure: shared by edge functions
 * (what is sent) and the CRM (the preview shows the same HTML).
 */
import {
  type BillingEmailBrand,
  type BillingEmailSection,
  formatEmailDate,
  renderBillingEmail,
  renderBillingEmailText,
} from "./billingEmailLayout.ts";
import { INVOICE_ORGANIZATION_EMAIL_MARK_URL } from "./invoiceOrganizationInfo.ts";
import {
  applyTemplateEdit,
  type MessageTemplateEdit,
} from "./messageTemplates.ts";

const contactLines = (brand: BillingEmailBrand) =>
  [brand.phone, brand.email, brand.website].filter((value): value is string =>
    Boolean(value?.trim()),
  );

/** "Invoice summary" rows: line items, discount, subtotal, fee, total. */
export const buildInvoiceSummarySection = (params: {
  lines: Array<{ description: string; amount: number }>;
  currency?: string | null;
  subtotal: number;
  feeAmount?: number;
  discountAmount?: number;
  total: number;
}): BillingEmailSection => {
  const money = (value: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: params.currency || "USD",
    }).format(Number(value) || 0);
  const feeAmount = Number(params.feeAmount) || 0;
  const discountAmount = Number(params.discountAmount) || 0;
  const rows: Array<[string, string]> = params.lines.map((line) => [
    // First line of a multi-line description is the item title.
    line.description.split("\n")[0]?.trim() || line.description,
    money(line.amount),
  ]);
  if (discountAmount > 0) rows.push(["Discount", `-${money(discountAmount)}`]);
  if (feeAmount > 0 || discountAmount > 0) {
    rows.push(["Subtotal", money(params.subtotal)]);
  }
  if (feeAmount > 0) rows.push(["Processing fee", money(feeAmount)]);
  rows.push(["Total", money(params.total)]);
  return { kind: "rows", title: "Invoice summary", rows };
};

/** Payment request (and reminder): amount, due date, Pay button, message. */
export const buildPaymentRequestEmail = (params: {
  brand: BillingEmailBrand;
  invoiceNumber: string;
  amountFormatted: string;
  dueDate?: string | null;
  paymentUrl: string;
  message: string;
  serviceLines?: string[];
  /** Extra sections after the services (e.g. line items and totals). */
  sections?: BillingEmailSection[];
  /** "Pay invoice" by default. */
  ctaLabel?: string;
}) => {
  const due = formatEmailDate(params.dueDate);
  const hero = {
    amount: params.amountFormatted,
    amountLabel: "Amount due",
    meta: [
      ...(due ? [`Due date: ${due}`] : []),
      `Invoice: ${params.invoiceNumber}`,
    ],
    cta: { label: params.ctaLabel ?? "Pay invoice", url: params.paymentUrl },
  };
  const services = (params.serviceLines ?? []).filter(Boolean);
  const sections: BillingEmailSection[] = [
    ...(services.length
      ? [{ kind: "list" as const, title: "Services", items: services }]
      : []),
    ...(params.sections ?? []),
  ];
  return {
    html: renderBillingEmail({
      brand: params.brand,
      preheader: `${params.amountFormatted}${due ? ` due ${due}` : ""} · Invoice ${params.invoiceNumber}`,
      hero,
      message: params.message,
      sections,
    }),
    text: renderBillingEmailText({
      brandName: params.brand.name,
      hero,
      message: params.message,
      sections,
      contact: contactLines(params.brand),
    }),
  };
};

/**
 * Files delivered after payment. With a portal link the files travel as
 * email attachments (`attached`) and the button opens the client portal;
 * without one, each file carries its own download URL.
 */
const isLbsDeliveryBrand = (brand: BillingEmailBrand) =>
  Boolean(brand.bannerUrl?.includes("latino-business-support")) ||
  /\b(lbs|latino business support)\b/i.test(brand.name);

const deliveryClientName = (name?: string | null) => {
  const trimmed = name?.trim();
  if (!trimmed || trimmed.includes("@")) return null;
  return trimmed;
};

export const buildFileDeliveryEmail = (params: {
  brand: BillingEmailBrand;
  invoiceNumber: string;
  propertyAddress: string;
  files: Array<{ name: string; url?: string | null; attached?: boolean }>;
  portalUrl?: string | null;
  /** Greeting name. An email address is ignored. */
  clientName?: string | null;
  /** What was charged, so a client charged on send is never surprised. */
  payment?: {
    status: "paid" | "clearing";
    amount: string;
    method?: string | null;
    date?: string | null;
    receiptUrl?: string | null;
  } | null;
  /** Company edit of "Files delivered email"; its text replaces the intro. */
  templateEdit?: MessageTemplateEdit | null;
}) => {
  const portalUrl = params.portalUrl?.trim() || null;
  const fileWord = params.files.length === 1 ? "file" : "files";
  const attachedCount = params.files.filter((file) => file.attached).length;
  const payment = params.payment ?? null;
  const hero = {
    ...(payment
      ? {
          status:
            payment.status === "paid"
              ? `Paid in full: ${payment.amount}`
              : `Bank payment in progress: ${payment.amount}. Your receipt follows in about 4 business days.`,
        }
      : {}),
    meta: [
      `Property: ${params.propertyAddress}`,
      `Invoice: ${params.invoiceNumber}`,
    ],
    cta: portalUrl
      ? { label: "View your files in your portal", url: portalUrl }
      : undefined,
  };
  // Attached files still read as links: each opens the portal copy.
  const listedFiles = params.files.map((file) => ({
    name: file.name,
    url: file.url ?? portalUrl,
  }));
  const paymentRows: Array<[string, string]> =
    payment && (payment.method || payment.date)
      ? [
          ...(payment.method
            ? [["Paid with", payment.method] as [string, string]]
            : []),
          ...(payment.date ? [["Date", payment.date] as [string, string]] : []),
        ]
      : [];
  const sections: BillingEmailSection[] = [
    {
      kind: "files" as const,
      title:
        portalUrl && attachedCount === params.files.length
          ? `Attached ${fileWord}`
          : `Your ${fileWord}`,
      files: listedFiles,
      note:
        !portalUrl && params.files.some((file) => file.url)
          ? "Links expire in 7 days, so save a copy to your device."
          : null,
    },
    ...(paymentRows.length
      ? [{ kind: "rows" as const, title: "Payment", rows: paymentRows }]
      : []),
    ...(payment?.status === "paid" && payment.receiptUrl
      ? [
          {
            kind: "files" as const,
            title: "Receipt",
            files: [{ name: "Payment receipt", url: payment.receiptUrl }],
          },
        ]
      : []),
  ];
  const message = !portalUrl
    ? `Your ${fileWord} for ${params.propertyAddress} ${params.files.length === 1 ? "is" : "are"} ready to download.`
    : attachedCount === params.files.length
      ? `Your ${fileWord} for ${params.propertyAddress} ${params.files.length === 1 ? "is" : "are"} attached to this email. You can also find ${params.files.length === 1 ? "it" : "them"} anytime in your portal.`
      : attachedCount > 0
        ? `Some of your files for ${params.propertyAddress} are attached to this email. The rest are too large for email and are waiting in your portal.`
        : `Your ${fileWord} for ${params.propertyAddress} ${params.files.length === 1 ? "is" : "are"} ready in your portal.`;
  const clientName = deliveryClientName(params.clientName);
  const edited = params.templateEdit?.body?.trim()
    ? applyTemplateEdit(
        "tickets.delivery.email",
        params.templateEdit,
        {
          client_first_name: clientName?.split(/\s+/)[0] ?? "",
          company_name: params.brand.name,
          property_address: params.propertyAddress,
          invoice_number: params.invoiceNumber,
          files_status: message,
        },
        { subject: "", body: message },
      ).body
    : null;
  const greeting = edited ? null : clientName ? `Hi ${clientName},` : "Hello,";
  const intro = edited ?? message;
  const lbs = isLbsDeliveryBrand(params.brand);
  const footerNote = lbs ? "También hablamos español." : null;
  return {
    html: renderBillingEmail({
      brand:
        lbs && !params.brand.logoUrl
          ? { ...params.brand, logoUrl: INVOICE_ORGANIZATION_EMAIL_MARK_URL }
          : params.brand,
      preheader: `Your ${fileWord} for ${params.propertyAddress} ${params.files.length === 1 ? "is" : "are"} ready.`,
      greeting,
      hero,
      message: intro,
      sections,
      replies: "welcome",
      footerNote,
    }),
    text: renderBillingEmailText({
      brandName: params.brand.name,
      hero,
      message: [greeting, intro].filter(Boolean).join("\n\n"),
      sections,
      contact: contactLines(params.brand),
      replies: "welcome",
    }),
  };
};

/** Payment receipt: amount paid, status, invoice summary. */
export const buildPaymentReceiptEmail = (params: {
  brand: BillingEmailBrand;
  invoiceNumber: string;
  paidTodayFormatted: string;
  paymentMethodHint?: string | null;
  totalPaidFormatted: string;
  invoiceTotalFormatted: string;
  balanceFormatted: string;
  paidInFull: boolean;
  invoiceUrl?: string | null;
  attachmentNote?: string | null;
  /** Company text (message template); defaults to the thank-you line. */
  intro?: string | null;
}) => {
  const hero = {
    meta: [
      `Invoice: ${params.invoiceNumber}`,
      ...(params.paymentMethodHint?.trim()
        ? [params.paymentMethodHint.trim()]
        : []),
    ],
    cta:
      !params.paidInFull && params.invoiceUrl
        ? { label: "View invoice", url: params.invoiceUrl }
        : null,
  };
  const message = [
    params.intro?.trim() || "Thank you for your payment.",
    params.attachmentNote ?? "Your receipt is attached as a PDF.",
    params.paidInFull
      ? "Your invoice is paid in full — no further action is needed."
      : null,
  ]
    .filter(Boolean)
    .join("\n\n");
  const sections = [
    {
      kind: "rows" as const,
      title: "Summary",
      rows: [
        ["Paid today", params.paidTodayFormatted],
        ["Total paid", params.totalPaidFormatted],
        ["Invoice total", params.invoiceTotalFormatted],
        params.paidInFull
          ? ["Status", "Paid in full"]
          : ["Balance due", params.balanceFormatted],
      ] as Array<[string, string]>,
    },
  ];
  return {
    html: renderBillingEmail({
      brand: params.brand,
      preheader: `Receipt for ${params.invoiceNumber} · ${params.paidTodayFormatted}`,
      hero,
      message,
      sections,
    }),
    text: renderBillingEmailText({
      brandName: params.brand.name,
      hero,
      message,
      sections,
      contact: contactLines(params.brand),
    }),
  };
};

/** Reminder or action-needed notice with a summary and one button. */
export const buildBillingNoticeEmail = (params: {
  brand: BillingEmailBrand;
  invoiceNumber: string;
  amountFormatted: string;
  status: string;
  statusTone?: "neutral" | "success" | "warning";
  message: string;
  ctaLabel: string;
  ctaUrl: string;
  rows?: Array<[string, string]>;
}) => {
  const hero = {
    amount: params.amountFormatted,
    status: params.status,
    meta: [`Invoice: ${params.invoiceNumber}`],
    cta: { label: params.ctaLabel, url: params.ctaUrl },
  };
  const sections = params.rows?.length
    ? [{ kind: "rows" as const, rows: params.rows }]
    : [];
  return {
    html: renderBillingEmail({
      brand: params.brand,
      preheader: `${params.status} · Invoice ${params.invoiceNumber}`,
      hero,
      message: params.message,
      sections,
    }),
    text: renderBillingEmailText({
      brandName: params.brand.name,
      hero,
      message: params.message,
      sections,
      contact: contactLines(params.brand),
    }),
  };
};
